import { gql, TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { ConfirmDialog } from '@repo/shared-client/components/blocks'
import { FormProvider, useForm } from '@repo/shared-client/form'
import { useTranslation } from '@web/src/lib/intl/react'
import { ArrowLeftRightIcon } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import SFProfileSearchField from 'src/components/primatives/sf-profile-search-field/sf-profile-search-field'
import { PlaceInfo } from 'src/lib/fragments'
import type { TransferPlaceMutation, TransferPlaceMutationVariables } from 'types/graphql'

const TRANSFER_PLACE_MUTATION: TypedDocumentNode<TransferPlaceMutation, TransferPlaceMutationVariables> = gql`
  mutation TransferPlaceMutation($placeIdOrSlug: String!, $input: UpdatePlaceInput!) {
    updatePlace(placeIdOrSlug: $placeIdOrSlug, input: $input) {
      ...PlaceInfo
    }
  }
  ${PlaceInfo}
`

type CustomModalProps = {
  open: boolean
  onOpenChange?: (details: { open: boolean }) => void
}

const PlaceTransferModal = ({
  placeIdOrSlug,
  onComplete,
  ...props
}: CustomModalProps & {
  placeIdOrSlug?: string
  onComplete?: () => void
}) => {
  const { t } = useTranslation()
  const formMethods = useForm()
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const [profile, setProfile] = useState(null)

  const [transferPlace] = useMutation(TRANSFER_PLACE_MUTATION, {
    onCompleted: () => {
      closeDialog()
      toast.success(t('Place ownership transferred successfully'))
      onComplete?.()
    },
    onError: (error) => {
      toast.error(error.message)
      closeDialog()
    },
  })

  const onSubmit = () => {
    setShowSpinnerValue(true)
    transferPlace({
      variables: { placeIdOrSlug, input: { ownerId: profile?.id } },
    })
  }

  const closeDialog = () => {
    props?.onOpenChange?.({ open: false })
  }

  return (
    <ConfirmDialog
      beforeControls={
        <FormProvider {...formMethods}>
          <SFProfileSearchField
            excludeSelf={false}
            inputProps={{ placeholder: t('Search profiles here...') }}
            label={t('Select New Owner')}
            name="profile"
            onProfileSelected={(profileData) => {
              setProfile(profileData ?? null)
              return false
            }}
          />
        </FormProvider>
      }
      confirmationPlaceholder={t('Type confirm')}
      confirmationPrompt={t("Enter 'confirm' in the box below")}
      confirmationText="confirm"
      confirmDisabled={!profile}
      confirmText={t('Transfer')}
      icon={<ArrowLeftRightIcon />}
      loading={showSpinner}
      onConfirm={onSubmit}
      onOpenChange={(details) => {
        props?.onOpenChange?.(details)

        if (!details.open) {
          formMethods.reset()
          setShowSpinnerValue(false)
          setProfile(null)
        }
      }}
      open={props.open}
      text={t('You will no longer be able to edit this page if you transfer ownership.')}
      title={t('Transfer Place Ownership')}
      variant="delete"
    />
  )
}

export default PlaceTransferModal
